import { BankTypeEnum, CardTypesEnum } from '@common-fe/common-fe';

import { Address } from '@/common/types';

import { PaymentMethodVerificationStatus } from '../ReimburseMe/types/payment.types';

export enum DefaultClaimPayeeType {
  participant = 'PARTICIPANT',
  provider = 'PROVIDER',
  employeeChoice = 'EMPLOYEE_CHOICE',
}

export enum PaymentMethodType {
  DIRECT_DEPOSIT = 'DIRECT_DEPOSIT',
  DEBIT = 'DEBIT',
  VENMO = 'VENMO',
  PAYPAL = 'PAYPAL',
  CHECK = 'CHECK',
}

export enum StatusNames {
  A = 'Active',
  T = 'Terminated',
  L = 'Leave of absence',
  R = 'Retirement',
  F = 'Future',
  C = 'COBRA',
}

export interface TransferAccountPayload {
  id?: number;
  name?: string;
  amount?: number;
  commuter_limit?: number | null;
  is_locked?: boolean;
  election_limit?: number | null;
  category_spend_limit?: number | null;
  person_spend_limit?: number | null;
  spend_period_type?: string;
  rollover_limit?: number | null;
}
export interface TransferAccountsPayload {
  recommended?: TransferAccountPayload[];
  other?: TransferAccountPayload[];
}
export interface TransferAccount {
  id?: string;
  name?: string;
  amount?: number;
  commuterLimit?: number;
  isLocked?: boolean;
  electionLimit?: number | null;
  categorySpendLimit?: number | null;
  personSpendLimit?: number | null;
  spendPeriodType?: string;
  rolloverLimit?: number | null;
}
export interface TransferAccounts {
  recommended?: TransferAccount[];
  other?: TransferAccount[];
}

export enum ClaimAutopayType {
  auto = 'AUTO',
  click = 'CLICK',
  employeeChoice = 'EMPLOYEE_CHOICE',
}

export enum EmployeeGenderType {
  male = 'M',
  female = 'F',
  other = 'O',
}

export enum EmployeeEmailType {
  home = 'HOME',
  work = 'WORK',
  other = 'OTHER',
}

export const PAYMENT_METHOD_TITLE = {
  [PaymentMethodType.DIRECT_DEPOSIT]: 'Direct Deposit',
  [PaymentMethodType.DEBIT]: 'My Personal Debit Card',
  [PaymentMethodType.VENMO]: 'Venmo',
  [PaymentMethodType.PAYPAL]: 'PayPal',
  [PaymentMethodType.CHECK]: 'Check',
};

export enum PaymentMethodOwnerType {
  ORGANIZATION = 'ORGANIZATION',
  EMPLOYEE = 'EMPLOYEE',
  PROVIDER = 'PROVIDER',
}

export enum PaymentStatus {
  VERIFIED = 'VERIFIED',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  VERIFICATION_FAILED = 'VERIFICATION_FAILED',
  NEW = 'NEW',
}

export interface PaymentMethodDtoPayload {
  id: number;
  payment_method_type?: PaymentMethodType;
  is_default?: boolean;
  account_id?: number;
  recipient_id?: number;
  email?: string;
  phone_number?: string;
  payment_method_status?: PaymentStatus;
  account_number?: string;
  account_description?: string;
  account_number_last4?: string;
  payment_address?: Address;
  payment_method_owner?: {
    organization_id?: number;
    payment_method_owner_type?: string;
    employee_id?: number;
    provider_id?: number;
  };
  requisites?: {
    phone_number?: string;
    email?: string;
    card_holder_name?: string;
    card_expiration_date?: string;
    card_last4?: string;
    card_type?: BankTypeEnum;
    account_number_last4?: string;
  };
  verification_status?: PaymentMethodVerificationStatus;
}

export interface PaymentMethod {
  isChecked?: boolean;
  id: string;
  type?: PaymentMethodType;
  isDefault?: boolean;
  accountId?: string;
  recipientId?: string;
  email?: string;
  phoneNumber?: string;
  status?: PaymentStatus;
  accountNumber?: string;
  accountDescription?: string;
  accountNumberLast4?: string;
  address?: Address;
  paymentMethodOwner?: {
    id?: string;
    ownerType?: PaymentMethodOwnerType;
    employeeId?: string;
    providerId?: string;
    organizationId?: string;
  };
  isVerified?: boolean;
  requisites?: {
    phoneNumber?: string;
    email?: string;
    cardHolderName?: string;
    cardExpirationDate?: string;
    cardLast4?: string;
    cardType?: BankTypeEnum;
  };
  verificationStatus?: PaymentMethodVerificationStatus;
}

export enum EmployeeAddressType {
  poBox = 'PO_BOX',
  residential = 'RESIDENTIAL',
  business = 'BUSINESS',
  unknown = 'UNKNOWN',
}

export enum SourceIds {
  SSN = 'SSN',
  EEID = 'EMPLOYER_EMPLOYEE_ID',
  PEID = 'PARTNER_EMPLOYEE_ID',
}

export const PaymentMethodTiming = {
  [PaymentMethodType.DIRECT_DEPOSIT]: '1-3 days once approved',
  [PaymentMethodType.CHECK]: '5-7 days once approved',
  [PaymentMethodType.PAYPAL]: 'Under 10 minutes once approved',
  [PaymentMethodType.DEBIT]: 'Under 10 minutes once approved',
  [PaymentMethodType.VENMO]: 'Under 10 minutes once approved',
};

export interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  status: string;
  ssn: string;
  last4Ssn?: string;
  employer: string;
  employerEmployeeId: string;
  partnerEmployeeId: string;
  isReimbursementAllowed?: boolean;
  employerName?: string;
  subsidiaryName?: string;
  isFraudReviewRequired?: boolean;
  fraudReviewRequiredAt?: string;
}

export interface EnrollmentBalance {
  id: string;
  type: CardTypesEnum;
  availableBalance: number;
  election: number;
  effectiveDate?: Date | null;
  spentItByDate?: Date | null;
  endDate?: Date | null;
  claimItByDate?: Date | null;
  contributions?: {
    employee: number;
    employer: number;
  };
}

export interface EmployeeEmailDto {
  id?: number;
  address: string;
  preferred: boolean;
  valid_from?: string;
  valid_to?: string;
  email_type?: EmployeeEmailType;
}

export interface EmployeeAddress {
  id?: string;
  line1?: string;
  line2?: string;
  line3?: string;
  line4?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  countryId?: string;
  addressType?: EmployeeAddressType;
  preferredMailing?: boolean;
  preferredPhysical?: boolean;
}

export interface EmployeeAddressPayload {
  id: number;
  line1: string;
  line2: string;
  line3: string;
  line4: string;
  city: string;
  state: string;
  zipcode: string;
  country_id: number;
  address_type: EmployeeAddressType;
  preferred_mailing?: boolean;
  preferred_physical?: boolean;
}

export interface EmployeePreferredAddressDto {
  address: EmployeeAddressPayload;
  valid_from: string;
  valid_to: string;
  preferred_mailing: boolean;
  preferred_physical: boolean;
}

export interface EmployeeOrganizationDto {
  communication_channels: string[];
  organization_id: number;
  organization_path: string;
  organization_name: string;
}

export interface EmployeeCommunicationConfigurationDto {
  communication_config_channels: string[];
  notification_priority: string;
}

export interface EmployeePersonalInfo {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: string;
  email: string;
  phoneCell: string;
  phoneHome: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  userName: string;
  password: string;
  claimAutopayType?: ClaimAutopayType;
  defaultClaimPayeeType?: DefaultClaimPayeeType;
  employerEmployeeId?: string | null;
  partnerEmployeeId: string | null;
  employerName: string;
  communicationChannels: string[];
  communicationConfigChannels?: string[];
  notificationPriority?: string;
  employerStatus: string;
  ssn: string;
  personId: string;
  uniqueId: string;
  payrollGroup: string;
  subsidiary: string;
  hireDate: string;
  terminationDate: string;
  organizationId: string;
  organizationPath: string;
}

export enum DependentStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}
export const CREATION_SOURCE_TYPE = 'WEB_ADMIN_PORTAL';

export enum CreationSourceType {
  API = 'API',
  DEPENDENT = 'DEPENDENT',
  CARRIER = 'CARRIER',
  WEB_PARTICIPANT_PORTAL = 'WEB_PARTICIPANT_PORTAL',
  WEB_ADMIN_PORTAL = 'WEB_ADMIN_PORTAL',
}

export interface DependentPayload {
  dependent_id?: number;
  employee_id?: number;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  gender_type?: string;
  birth_on?: string;
  relationship_type?: string;
  creation_source_type?: CreationSourceType;
  status?: DependentStatusEnum;
}

export const DEPENDENT_RELATIONSHIP_ME = 'Employee';
export interface Dependent {
  dependentId?: string;
  employeeId?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  fullName?: string;
  ssn?: string;
  genderType?: string;
  birthOn?: string;
  relationshipType?: string;
  creationSourceType?: CreationSourceType;
  status?: DependentStatusEnum;
}

export enum InvestorStatus {
  INVESTOR_CREATED = 'INVESTOR_CREATED',
  INVESTMENT_PATH_SELECTED = 'INVESTMENT_PATH_SELECTED',
  QUESTIONNAIRE_FILLED = 'QUESTIONNAIRE_FILLED',
  INVESTMENT_AMOUNT_SELECTED = 'INVESTMENT_AMOUNT_SELECTED',
  PENDING_EXTERNAL_CREATION = 'PENDING_EXTERNAL_CREATION',
  ACTIVE = 'ACTIVE',
}
